import { useEffect, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  User, Phone, Briefcase, Heart, CreditCard, Star, Building2, Settings2, FileText, Receipt, Handshake, Landmark,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MaskedInput } from "@/components/ui/masked-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { personSchema, type PersonFormData } from "../schemas/personSchema";
import { useCustomerGroups } from "../hooks/useCustomerGroups";
import { useRoutes } from "../hooks/useRoutes";
import { usePriceTables } from "../hooks/usePriceTables";
import { useSalespeople } from "../hooks/useSalespeople";
import { useProductGroups } from "@/features/produtos/hooks/useProductGroups";
import { ProdutoSearchCombobox } from "@/components/ProdutoSearchCombobox";

interface PersonFormProps {
  onSubmit: (data: PersonFormData) => void;
  defaultValues?: PersonFormData | null;
  isLoading?: boolean;
}

// Aba onde cada campo validado mora — usado para levar o usuário até o erro.
// Sem isso, um campo obrigatório numa aba fechada (ex.: Tabela de Preço, em
// Comercial) barra o submit sem nenhum aviso visível.
const TAB_ORDER = [
  "geral", "tipos", "contato", "pessoal", "documentos", "comercial", "fiscal",
  "bancario", "funcionario", "fidelidade", "relacionamento", "observacoes",
] as const;

const FIELD_TAB: Record<string, (typeof TAB_ORDER)[number]> = {
  person_type: "geral", name: "geral", nickname: "geral", is_active: "geral",
  email: "contato", phone: "contato", mobile: "contato", whatsapp: "contato",
  contact_person_name: "contato", contact_person_phone: "contato",
  contact_person_mobile: "contato", contact_person_residential: "contato", referral_person: "contato",
  birth_date: "pessoal", birthplace: "pessoal", gender: "pessoal", civil_status: "pessoal",
  profession: "pessoal", father_name: "pessoal", mother_name: "pessoal", spouse_name: "pessoal",
  spouse_phone: "pessoal", spouse_mobile: "pessoal", spouse_landline: "pessoal",
  cpf_cnpj: "documentos", ie: "documentos", im: "documentos", rg: "documentos",
  rg_issuer: "documentos", rg_issue_date: "documentos", driver_license: "documentos", work_card: "documentos",
  customer_group_id: "comercial", salesperson_id: "comercial", route_id: "comercial",
  billing_group_id: "comercial", price_table_id: "comercial", discount_percentage: "comercial",
  credit_limit: "comercial", spc_situation: "comercial", credit_block_reason: "comercial",
  invoice_due_days: "comercial", invoice_closing_day: "comercial",
  tax_regime: "fiscal", ie_indicator: "fiscal", gov_agency_type: "fiscal", cbs_reduction_gov: "fiscal",
  construction_id: "fiscal", art_id: "fiscal",
  ir_percentage: "fiscal", csll_percentage: "fiscal", inss_percentage: "fiscal",
  pis_percentage: "fiscal", cofins_percentage: "fiscal", issqn_percentage: "fiscal",
  supplier_bank_code: "bancario", supplier_agency: "bancario", supplier_agency_dv: "bancario",
  supplier_account: "bancario", supplier_account_dv: "bancario",
  admission_date: "funcionario", job_function: "funcionario", salary: "funcionario",
  commission_percentage: "funcionario",
  loyalty_points: "fidelidade", loyalty_points_value: "fidelidade",
  preferred_product_id: "relacionamento", preferred_group_id: "relacionamento",
  product_classification: "relacionamento", desired_product: "relacionamento",
  last_purchase_date: "relacionamento", how_met: "relacionamento", complaint: "relacionamento",
  customer_classification: "relacionamento", preferred_size: "relacionamento",
  preferred_color: "relacionamento", preferred_flavor: "relacionamento",
  notes: "observacoes",
};

const TAB_LABEL: Record<(typeof TAB_ORDER)[number], string> = {
  geral: "Geral", tipos: "Tipos", contato: "Contato", pessoal: "Pessoal", documentos: "Documentos",
  comercial: "Comercial", fiscal: "Fiscal", bancario: "Bancário", funcionario: "Funcionário",
  fidelidade: "Fidelidade", relacionamento: "Relacionamento", observacoes: "Observações",
};

// Helper to render a Switch field inside a bordered row
const SwitchRow = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <div className="flex items-center justify-between rounded-lg border p-3 gap-4">
    <div>
      <p className="text-sm font-medium leading-none">{label}</p>
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

export const PersonForm = ({ onSubmit, defaultValues, isLoading }: PersonFormProps) => {
  const { groups: customerGroups } = useCustomerGroups();
  const { activeRoutes } = useRoutes();
  const { priceTables } = usePriceTables();
  const { salespeople } = useSalespeople();
  const { groups: productGroups } = useProductGroups();

  const form = useForm<PersonFormData>({
    resolver: zodResolver(personSchema),
    defaultValues: defaultValues ?? {
      person_type:         "fisica",
      is_active:           true,
      is_customer:         false,
      is_supplier:         false,
      is_final_consumer:   true,
      allows_credit_sales: true,
      is_exportable:       true,
    },
  });

  // Re-populate form when editing (data loads async after component mounts)
  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  const personType = form.watch("person_type");
  const isPF       = personType !== "juridica";

  const [activeTab, setActiveTab] = useState<string>("geral");

  // Uma única tabela de preço ativa: já vem selecionada — no cadastro novo e também ao editar
  // quem ficou sem tabela (ex.: pessoa importada por planilha), senão o Salvar trava no campo.
  useEffect(() => {
    const ativas = priceTables?.filter(p => p.isAtiva) ?? [];
    if (ativas.length === 1 && !form.getValues("price_table_id")) {
      form.setValue("price_table_id", ativas[0].id);
    }
  }, [priceTables, defaultValues, form]);

  const handleInvalid = (errors: FieldErrors<PersonFormData>) => {
    const campos = Object.keys(errors);
    const abas = TAB_ORDER.filter(t => campos.some(c => (FIELD_TAB[c] ?? "geral") === t));
    const primeira = abas[0] ?? "geral";
    setActiveTab(primeira);
    const primeiroErro = campos.find(c => (FIELD_TAB[c] ?? "geral") === primeira);
    const msg = primeiroErro
      ? (errors[primeiroErro as keyof PersonFormData]?.message as string | undefined)
      : undefined;
    toast.error("Não foi possível salvar: verifique os campos destacados", {
      description: `${msg ? msg + " — " : ""}aba(s): ${abas.map(a => TAB_LABEL[a]).join(", ")}`,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, handleInvalid)} className="space-y-6">

        {/* ── Horizontal tab layout (mesmo padrão do cadastro de produtos) ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          <TabsList className="flex flex-wrap h-auto gap-1 mb-6">
            <TabsTrigger value="geral" className="gap-2">
              <User className="h-3.5 w-3.5 shrink-0" />Geral
            </TabsTrigger>
            <TabsTrigger value="tipos" className="gap-2">
              <Settings2 className="h-3.5 w-3.5 shrink-0" />Tipos
            </TabsTrigger>
            <TabsTrigger value="contato" className="gap-2">
              <Phone className="h-3.5 w-3.5 shrink-0" />Contato
            </TabsTrigger>
            {isPF && (
              <TabsTrigger value="pessoal" className="gap-2">
                <Heart className="h-3.5 w-3.5 shrink-0" />Pessoal
              </TabsTrigger>
            )}
            <TabsTrigger value="documentos" className="gap-2">
              <CreditCard className="h-3.5 w-3.5 shrink-0" />Documentos
            </TabsTrigger>
            <TabsTrigger value="comercial" className="gap-2">
              <Building2 className="h-3.5 w-3.5 shrink-0" />Comercial
            </TabsTrigger>
            <TabsTrigger value="fiscal" className="gap-2">
              <Receipt className="h-3.5 w-3.5 shrink-0" />Fiscal
            </TabsTrigger>
            <TabsTrigger value="bancario" className="gap-2">
              <Landmark className="h-3.5 w-3.5 shrink-0" />Bancário
            </TabsTrigger>
            <TabsTrigger value="funcionario" className="gap-2">
              <Briefcase className="h-3.5 w-3.5 shrink-0" />Funcionário
            </TabsTrigger>
            <TabsTrigger value="fidelidade" className="gap-2">
              <Star className="h-3.5 w-3.5 shrink-0" />Fidelidade
            </TabsTrigger>
            <TabsTrigger value="relacionamento" className="gap-2">
              <Handshake className="h-3.5 w-3.5 shrink-0" />Relacionamento
            </TabsTrigger>
            <TabsTrigger value="observacoes" className="gap-2">
              <FileText className="h-3.5 w-3.5 shrink-0" />Recado
            </TabsTrigger>
          </TabsList>

          <div className="space-y-4">

            {/* ── Geral ──────────────────────────────────────────────── */}
            <TabsContent value="geral" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="person_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="fisica">Pessoa Física</SelectItem>
                          <SelectItem value="juridica">Pessoa Jurídica</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>{personType === "juridica" ? "Razão Social *" : "Nome Completo *"}</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="nickname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{personType === "juridica" ? "Nome Fantasia" : "Apelido"}</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <SwitchRow
                        label="Cadastro Ativo"
                        checked={field.value ?? true}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Tipos ──────────────────────────────────────────────── */}
            <TabsContent value="tipos" className="mt-0 space-y-3">
              <p className="text-sm text-muted-foreground mb-2">Defina os papéis desta pessoa no sistema.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {([
                  ["is_customer",      "Cliente",         "Compra produtos/serviços"],
                  ["is_supplier",      "Fornecedor",      "Fornece produtos/serviços"],
                  ["is_employee",      "Funcionário",     "Colaborador da empresa"],
                  ["is_driver",        "Motorista",       "Realiza entregas"],
                  ["is_sales_rep",     "Vendedor",        "Realiza vendas"],
                  ["is_fleet_owner",   "Frotista",        "Possui frota de veículos"],
                  ["is_accountant",    "Contador",        undefined],
                  ["is_guest",         "Hóspede",         undefined],
                  ["is_operator",      "Operadora",       undefined],
                  ["is_final_consumer","Consumidor Final", "NF-e: tributação ao consumidor"],
                  ["is_exportable",    "Exportável",      "Aparece em integrações"],
                ] as [keyof PersonFormData, string, string | undefined][]).map(([name, label, desc]) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label={label}
                          description={desc}
                          checked={!!field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </TabsContent>

            {/* ── Contato ────────────────────────────────────────────── */}
            <TabsContent value="contato" className="mt-0 space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl><Input type="email" {...field} value={field.value ?? ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="opt_in_marketing"
                render={({ field }) => (
                  <FormItem>
                    <SwitchRow
                      label="Aceita receber marketing (WhatsApp)"
                      description="Consentimento LGPD para campanhas/promoções. Não afeta avisos transacionais (nota, boleto, OS)."
                      checked={field.value ?? true}
                      onChange={field.onChange}
                    />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone Fixo</FormLabel>
                      <FormControl>
                        <MaskedInput mask="phone" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 0000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mobile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Celular</FormLabel>
                      <FormControl>
                        <MaskedInput mask="mobile" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 00000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="whatsapp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>WhatsApp</FormLabel>
                      <FormControl>
                        <MaskedInput mask="mobile" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 00000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />
              <p className="text-sm font-medium">Pessoa de Recado</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="contact_person_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact_person_phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <MaskedInput mask="phone" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 0000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact_person_mobile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Celular</FormLabel>
                      <FormControl>
                        <MaskedInput mask="mobile" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 00000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact_person_residential"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Residencial</FormLabel>
                      <FormControl>
                        <MaskedInput mask="phone" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 0000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="referral_person"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pessoa que Indicou</FormLabel>
                    <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TabsContent>

            {/* ── Pessoal (PF only) ──────────────────────────────────── */}
            <TabsContent value="pessoal" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="birth_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Nascimento</FormLabel>
                      <FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="birthplace"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Naturalidade</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sexo</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="masculino">Masculino</SelectItem>
                          <SelectItem value="feminino">Feminino</SelectItem>
                          <SelectItem value="outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="civil_status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado Civil</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="solteiro">Solteiro(a)</SelectItem>
                          <SelectItem value="casado">Casado(a)</SelectItem>
                          <SelectItem value="divorciado">Divorciado(a)</SelectItem>
                          <SelectItem value="viuvo">Viúvo(a)</SelectItem>
                          <SelectItem value="uniao_estavel">União Estável</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="profession"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Profissão</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />
              <p className="text-sm font-medium">Filiação</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="father_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Pai</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mother_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome da Mãe</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />
              <p className="text-sm font-medium">Cônjuge</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="spouse_name"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Nome do Cônjuge</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="spouse_phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone Cônjuge</FormLabel>
                      <FormControl>
                        <MaskedInput mask="phone" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 0000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="spouse_mobile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Celular Cônjuge</FormLabel>
                      <FormControl>
                        <MaskedInput mask="mobile" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 00000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="spouse_landline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fixo Cônjuge</FormLabel>
                      <FormControl>
                        <MaskedInput mask="phone" value={field.value ?? ""} onChange={field.onChange} placeholder="(00) 0000-0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Documentos ─────────────────────────────────────────── */}
            <TabsContent value="documentos" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cpf_cnpj"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{personType === "juridica" ? "CNPJ" : "CPF"}</FormLabel>
                      <FormControl>
                        <MaskedInput mask="cpf_cnpj" value={field.value ?? ""} onChange={field.onChange} placeholder={personType === "juridica" ? "00.000.000/0000-00" : "000.000.000-00"} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ie"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inscrição Estadual</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="im"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inscrição Municipal</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {isPF && (
                <>
                  <Separator />
                  <p className="text-sm font-medium">Documentos Pessoais</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="rg"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>RG</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="rg_issuer"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Órgão Emissor</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="rg_issue_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Data Emissão RG</FormLabel>
                          <FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="driver_license"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CNH (Habilitação)</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="work_card"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Carteira de Trabalho</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              )}
            </TabsContent>

            {/* ── Comercial ──────────────────────────────────────────── */}
            <TabsContent value="comercial" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="customer_group_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Grupo de Clientes</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                        value={field.value != null ? String(field.value) : ""}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          {customerGroups?.filter(g => !g.isInativo).map((g) => (
                            <SelectItem key={g.id} value={String(g.id)}>{g.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="salesperson_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vendedor (carteira)</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}
                        value={field.value ? String(field.value) : "__none__"}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione o vendedor..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhum —</SelectItem>
                          {salespeople.map((v) => (
                            <SelectItem key={v.id} value={String(v.id)}>{v.nomeRazao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="route_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rota</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}
                        value={field.value ? String(field.value) : "__none__"}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhuma —</SelectItem>
                          {activeRoutes?.map((r) => (
                            <SelectItem key={r.id} value={String(r.id)}>{r.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="billing_group_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Grupo de Cobrança</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))}
                        value={field.value ? String(field.value) : "__none__"}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhum (sem venda a prazo) —</SelectItem>
                          {customerGroups?.filter(g => !g.isInativo).map((g) => (
                            <SelectItem key={g.id} value={String(g.id)}>{g.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="price_table_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tabela de Preço <span className="text-destructive">*</span></FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                        value={field.value != null ? String(field.value) : ""}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          {priceTables?.filter(p => p.isAtiva).map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>{p.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="discount_percentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>% Desconto Padrão</FormLabel>
                      <FormControl><Input type="number" step="0.01" min="0" max="100" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="credit_limit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Limite de Crédito (R$)</FormLabel>
                      <FormControl><Input type="number" step="0.01" min="0" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="spc_situation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Situação SPC</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="R">Regular</SelectItem>
                          <SelectItem value="I">Irregular</SelectItem>
                          <SelectItem value="N">Não Consultado</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="allows_credit_sales"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow label="Permite Venda a Prazo" checked={field.value ?? true} onChange={field.onChange} />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="credit_blocked"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow label="Bloquear Crédito" checked={!!field.value} onChange={field.onChange} />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="credit_block_reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Motivo do Bloqueio</FormLabel>
                    <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />
              <p className="text-sm font-medium">Faturamento</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="invoice_due_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dias para Vencimento</FormLabel>
                      <FormControl><Input type="number" min="0" {...field} value={field.value ?? 0} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="invoice_closing_day"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dia de Fechamento</FormLabel>
                      <FormControl><Input type="number" min="0" max="31" {...field} value={field.value ?? 0} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="invoice_on_last_day"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="block mb-2">Último Dia do Mês</FormLabel>
                      <SwitchRow label="Fatura no último dia" checked={!!field.value} onChange={field.onChange} />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Fiscal ─────────────────────────────────────────────── */}
            <TabsContent value="fiscal" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="tax_regime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Regime Tributário</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                        value={field.value != null ? String(field.value) : ""}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="1">Simples Nacional</SelectItem>
                          <SelectItem value="2">Simples Nacional — excesso de sublimite</SelectItem>
                          <SelectItem value="3">Regime Normal (Lucro Real/Presumido)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ie_indicator"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Indicador da IE (NF-e)</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                        value={field.value != null ? String(field.value) : ""}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="1">1 — Contribuinte ICMS</SelectItem>
                          <SelectItem value="2">2 — Contribuinte isento de IE</SelectItem>
                          <SelectItem value="9">9 — Não contribuinte</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />
              <p className="text-sm font-medium">Retenções na Fonte (serviços / órgãos públicos)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {([
                  ["withhold_ir",     "ir_percentage",     "IR"],
                  ["withhold_csll",   "csll_percentage",   "CSLL"],
                  ["withhold_inss",   "inss_percentage",   "INSS"],
                  ["withhold_pis",    "pis_percentage",    "PIS"],
                  ["withhold_cofins", "cofins_percentage", "COFINS"],
                  ["withhold_issqn",  "issqn_percentage",  "ISSQN"],
                ] as const).map(([flag, perc, label]) => (
                  <div key={flag} className="flex items-end gap-3 rounded-lg border p-3">
                    <FormField
                      control={form.control}
                      name={flag}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <SwitchRow label={`Reter ${label}`} checked={!!field.value} onChange={field.onChange} />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={perc}
                      render={({ field }) => (
                        <FormItem className="w-28">
                          <FormLabel className="text-xs">%</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" min="0" max="100" {...field} value={field.value ?? 0} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                ))}
              </div>

              <Separator />
              <p className="text-sm font-medium">Órgão Público / Obra (NFS-e)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="gov_agency_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Órgão Governamental (código)</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" {...field} value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cbs_reduction_gov"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>% Redução CBS (órgão público)</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" min="0" max="100" {...field} value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="construction_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ID da Obra</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={20} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="art_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ID ART</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={20} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Bancário (favorecido em remessa CNAB) ──────────────── */}
            <TabsContent value="bancario" className="mt-0 space-y-4">
              <p className="text-sm text-muted-foreground">
                Conta bancária do favorecido. Usada como destino do crédito no
                Segmento A da remessa CNAB quando esta pessoa é fornecedor pago em lote.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="supplier_bank_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Banco (código FEBRABAN)</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={10} placeholder="Ex.: 001, 237, 341" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="supplier_agency"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Agência (sem dígito)</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={20} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="supplier_agency_dv"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>DV agência</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={2} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="supplier_account"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Conta (sem dígito)</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={20} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="supplier_account_dv"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>DV conta</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} maxLength={2} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Funcionário ────────────────────────────────────────── */}
            <TabsContent value="funcionario" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="admission_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Admissão</FormLabel>
                      <FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="job_function"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Função</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="salary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Salário (R$)</FormLabel>
                      <FormControl><Input type="number" step="0.01" min="0" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="commission_percentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>% Comissão</FormLabel>
                      <FormControl><Input type="number" step="0.01" min="0" max="100" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Fidelidade ─────────────────────────────────────────── */}
            <TabsContent value="fidelidade" className="mt-0 space-y-4">
              <FormField
                control={form.control}
                name="earns_loyalty_points"
                render={({ field }) => (
                  <FormItem>
                    <SwitchRow
                      label="Pontua Fidelidade"
                      description="Esta pessoa acumula pontos de fidelidade"
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="loyalty_points"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pontos Acumulados</FormLabel>
                      <FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="loyalty_points_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor dos Pontos (R$)</FormLabel>
                      <FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </TabsContent>

            {/* ── Relacionamento (CRM) ───────────────────────────────── */}
            <TabsContent value="relacionamento" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField control={form.control} name="preferred_product_id" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Produto de preferência</FormLabel>
                    <FormControl>
                      <ProdutoSearchCombobox value={field.value || undefined} onChange={(v) => field.onChange(v || null)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="preferred_group_id" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grupo de Produtos de preferência</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === "__none__" ? null : Number(v))} value={field.value ? String(field.value) : "__none__"}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">— Nenhum —</SelectItem>
                        {productGroups?.filter(g => !g.isInativo).map((g) => (
                          <SelectItem key={g.id} value={String(g.id)}>{g.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="product_classification" render={({ field }) => (
                  <FormItem><FormLabel>Classificação do produto</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="desired_product" render={({ field }) => (
                  <FormItem><FormLabel>Produto que deseja comprar</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="last_purchase_date" render={({ field }) => (
                  <FormItem><FormLabel>Data da última compra</FormLabel><FormControl><Input type="date" {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="how_met" render={({ field }) => (
                  <FormItem><FormLabel>Como tomou conhecimento da empresa?</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="complaint" render={({ field }) => (
                  <FormItem><FormLabel>O que desagradou o cliente na empresa?</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="customer_classification" render={({ field }) => (
                  <FormItem><FormLabel>Classificação para cliente</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="preferred_size" render={({ field }) => (
                  <FormItem><FormLabel>Tamanho</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="preferred_color" render={({ field }) => (
                  <FormItem><FormLabel>Cor de preferência</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="preferred_flavor" render={({ field }) => (
                  <FormItem><FormLabel>Tonalidade / Sabor</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                )} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <FormField control={form.control} name="gave_up_purchase" render={({ field }) => (
                  <FormItem><SwitchRow label="Desistiu da compra" checked={field.value ?? false} onChange={field.onChange} /></FormItem>
                )} />
                <FormField control={form.control} name="placed_order" render={({ field }) => (
                  <FormItem><SwitchRow label="Fez pedido" checked={field.value ?? false} onChange={field.onChange} /></FormItem>
                )} />
                <FormField control={form.control} name="already_served" render={({ field }) => (
                  <FormItem><SwitchRow label="Já foi atendido" checked={field.value ?? false} onChange={field.onChange} /></FormItem>
                )} />
                <FormField control={form.control} name="needs_contact" render={({ field }) => (
                  <FormItem><SwitchRow label="Precisa fazer contato" checked={field.value ?? false} onChange={field.onChange} /></FormItem>
                )} />
              </div>
            </TabsContent>

            {/* ── Recado / Observações ───────────────────────────────── */}
            <TabsContent value="observacoes" className="mt-0 space-y-4">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Observações</FormLabel>
                    <FormControl><Textarea {...field} value={field.value ?? ""} rows={4} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TabsContent>

          </div>{/* end content panel */}
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
